import { useState, useEffect, useRef, FormEvent, useCallback, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SAMPLE_STATUS } from '../../constants/sample.constants';
import { SampleListParams } from '../../services/sample.service';
import { storageRoomService } from '../../services/storageRoom.service';
import { countActiveSampleDetailedFilters, listSampleDetailedFilterChips } from '../../utils/countActiveSampleDetailedFilters';
import {
  foldNameFilterIntoSearch,
  normalizeSampleFiltersForUi,
} from '../../utils/normalizeSampleFiltersForUi';
import {
  isNarrowViewportNow,
  useListFirstDetailedFilterOnMount,
  usePersistedBoolean,
} from '../../hooks/usePersistedBoolean';
import {
  buildStorageRoomSelectLabels,
  formatStorageRoomSelectOption,
} from '../../utils/storageRoomSearch';
import KeywordDetailedFilterBar from './KeywordDetailedFilterBar';
import SampleDetailedFilterForm, {
  hasSampleMoreFilters,
} from './SampleDetailedFilterForm';

/** 一覧・レポートで共有（同じ開閉状態を維持） */
export const SAMPLE_DETAILED_FILTER_STORAGE_KEY = 'sample_filter_detailed_open';
export const SAMPLE_CASE_DETAILED_FILTER_STORAGE_KEY = 'sample_case_filter_detailed_open';

const FILTER_DEBOUNCE_MS = 300;

const HSCROLL_FADE_SELECTOR = '.sample-detailed-filter-primary, .sample-filter-chips';

/** 横スクロール端では右フェードを消し、スクロール不能なら最初から付けない */
function syncHorizScrollFade(el: HTMLElement) {
  const max = el.scrollWidth - el.clientWidth;
  const scrollable = max > 2;
  el.classList.toggle('is-scrollable', scrollable);
  // 非スクロール時は is-scrollable 無しで CSS がフェードオフ。端到達のみ is-scroll-end
  el.classList.toggle('is-scroll-end', scrollable && el.scrollLeft >= max - 2);
}

interface SampleFilterPanelProps {
  filters: SampleListParams;
  onFilterChange: (filters: SampleListParams) => void;
  onClear: () => void;
  isAdmin?: boolean;
  /** ケース管理など庫内位置が固定の画面では保管庫・引き出し・ケースを非表示 */
  hideLocationFilters?: boolean;
  /** 詳細フィルター開閉状態の localStorage キー（画面ごとに分離） */
  detailedFilterStorageKey?: string;
  /** 未保存時の詳細フィルター初期表示（デフォルト: 閉） */
  defaultDetailedFilterOpen?: boolean;
  /** 管理者の保管庫一覧 API（全件 / 自分＋共有） */
  adminStorageViewAll?: boolean;
  /** キーワードバー先頭（非推奨: 縦スペースを取る。一覧は trailing を優先） */
  toolbar?: ReactNode;
  /** キーワードと詳細トグルのあいだ（統計ストリップ等。表示制御は呼び出し側） */
  mid?: ReactNode;
  /** 詳細トグル右側（コンパクトな管理者スイッチなど） */
  trailing?: ReactNode;
  /** 詳細パネル先頭（モバイルでフィルター行から外した管理者スイッチなど） */
  detailedPreface?: ReactNode;
  /** KeywordDetailedFilterBar の見た目（ケース詳細は plain） */
  barVariant?: 'card' | 'plain';
  /** キーワード aria（ケース画面では「サンプル検索」などツリーと区別） */
  keywordAriaLabel?: string;
}

export default function SampleFilterPanel({
  filters,
  onFilterChange,
  onClear,
  isAdmin = false,
  hideLocationFilters = false,
  detailedFilterStorageKey = SAMPLE_DETAILED_FILTER_STORAGE_KEY,
  defaultDetailedFilterOpen = false,
  adminStorageViewAll = false,
  toolbar,
  mid,
  trailing,
  detailedPreface,
  barVariant = 'card',
  keywordAriaLabel,
}: SampleFilterPanelProps) {
  const { t, i18n } = useTranslation();
  const [localFilters, setLocalFilters] = useState<SampleListParams>(() =>
    normalizeSampleFiltersForUi(filters)
  );
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFiltersRef = useRef<SampleListParams | null>(null);
  const detailedCountOnMount = countActiveSampleDetailedFilters(filters, { hideLocationFilters });
  const [detailedFilterOpen, setDetailedFilterOpen, toggleDetailedFilter] = usePersistedBoolean(
    detailedFilterStorageKey,
    defaultDetailedFilterOpen,
    { isNarrow: isNarrowViewportNow(), activeCount: detailedCountOnMount }
  );
  useListFirstDetailedFilterOnMount(
    setDetailedFilterOpen,
    detailedCountOnMount,
    detailedFilterStorageKey
  );
  const { data: storageRoomsData } = useQuery({
    queryKey: ['storage-rooms', { limit: 1000, adminViewAll: adminStorageViewAll }],
    queryFn: () =>
      storageRoomService.list({
        limit: 1000,
        adminViewAll: adminStorageViewAll || undefined,
      }),
  });
  const storageRooms = storageRoomsData?.data || [];
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767.98px)');
    const sync = () => setIsNarrowViewport(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // 親からの外部変更（廃棄ヒント／利用可能のみ復元）と debounce pending の競合を防ぐ
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    pendingFiltersRef.current = null;
    setLocalFilters(normalizeSampleFiltersForUi(filters));
  }, [filters]);

  // 旧 name 条件が親に残っていればキーワードへ一度畳む
  useEffect(() => {
    if (!filters.name) return;
    onFilterChange(foldNameFilterIntoSearch(filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- name 孤児の移行のみ
  }, [filters.name]);

  const scheduleApplyPending = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      const next = pendingFiltersRef.current;
      if (next) onFilterChange(next);
    }, FILTER_DEBOUNCE_MS);
  }, [onFilterChange]);

  /** テキスト・日付: local 更新＋デバウンス適用（キーワードと同じモデル） */
  const handleDebouncedChange = useCallback(
    (field: keyof SampleListParams, value: string | number | boolean | undefined) => {
      const cleaned =
        value === '' || value === undefined || value === false ? undefined : value;
      setLocalFilters((prev) => {
        const next = { ...prev, [field]: cleaned };
        pendingFiltersRef.current = next;
        return next;
      });
      scheduleApplyPending();
    },
    [scheduleApplyPending]
  );

  /** セレクトは API に即反映（onFilterChange は updater 外 — StrictMode 二重呼び出し回避） */
  const handleSelectFilterChange = useCallback(
    (field: keyof SampleListParams, value: string | number | undefined) => {
      const cleaned = value === '' || value === undefined ? undefined : value;
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      let next: SampleListParams | null = null;
      setLocalFilters((prev) => {
        next = { ...prev, [field]: cleaned };
        pendingFiltersRef.current = next;
        return next;
      });
      if (next) onFilterChange(next);
    },
    [onFilterChange]
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    onFilterChange(localFilters);
  };

  const handleClear = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    pendingFiltersRef.current = {};
    setLocalFilters({});
    onClear();
  };

  const handleKeywordChange = (value: string) => {
    setLocalFilters((prev) => {
      const next = { ...prev, search: value || undefined };
      pendingFiltersRef.current = next;
      return next;
    });
    scheduleApplyPending();
  };

  const handleNotesChange = useCallback(
    (notes: string | undefined) => {
      setLocalFilters((prev) => {
        const next = {
          ...prev,
          notes,
          ...(notes ? { notesIsEmpty: undefined } : {}),
        };
        pendingFiltersRef.current = next;
        return next;
      });
      scheduleApplyPending();
    },
    [scheduleApplyPending]
  );

  const handleNotesIsEmptyChange = useCallback(
    (isEmpty: boolean) => {
      setLocalFilters((prev) => {
        const next = {
          ...prev,
          notesIsEmpty: isEmpty || undefined,
          ...(isEmpty ? { notes: undefined } : {}),
        };
        pendingFiltersRef.current = next;
        return next;
      });
      scheduleApplyPending();
    },
    [scheduleApplyPending]
  );

  const handleClearChip = useCallback(
    (keys: (keyof SampleListParams)[]) => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      let next: SampleListParams | null = null;
      setLocalFilters((prev) => {
        next = { ...prev };
        for (const key of keys) {
          delete next[key];
        }
        // ステータスチップ解除 → 既定の利用可能表示に戻す（全件露出を避ける）
        if (keys.includes('status')) {
          next.status = SAMPLE_STATUS.AVAILABLE;
        }
        pendingFiltersRef.current = next;
        return next;
      });
      if (next) onFilterChange(next);
    },
    [onFilterChange]
  );

  // バッジ／クリアは親の確定条件を正とする（復元直後に local が一瞬遅れても幽霊バッジを出さない）
  const appliedFilters = normalizeSampleFiltersForUi(filters);
  const detailedCount = countActiveSampleDetailedFilters(appliedFilters, { hideLocationFilters });
  const moreFiltersOpen = hasSampleMoreFilters(localFilters, isAdmin);
  const storageSelectLabels = useMemo(
    () => buildStorageRoomSelectLabels(storageRooms, i18n.language),
    [storageRooms, i18n.language]
  );
  const activeChips =
    !detailedFilterOpen && detailedCount > 0
      ? listSampleDetailedFilterChips(appliedFilters, t, {
          hideLocationFilters,
          storageRoomLabel: (id) => {
            const room = storageRooms.find((r) => r.id === id);
            return room
              ? formatStorageRoomSelectOption(room, storageSelectLabels, t)
              : String(id);
          },
        })
      : [];
  const panelRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = panelRootRef.current;
    if (!root) return;

    let raf = 0;
    const syncAll = () => {
      cancelAnimationFrame(raf);
      // Collapse 展開直後は scrollWidth が未確定なことがあるため2フレーム待つ
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          root.querySelectorAll<HTMLElement>(HSCROLL_FADE_SELECTOR).forEach(syncHorizScrollFade);
        });
      });
    };
    const onScroll = (e: Event) => {
      const target = e.target;
      if (target instanceof HTMLElement && target.matches(HSCROLL_FADE_SELECTOR)) {
        syncHorizScrollFade(target);
      }
    };

    root.addEventListener('scroll', onScroll, true);
    syncAll();
    const mo = new MutationObserver(syncAll);
    mo.observe(root, { childList: true, subtree: true });
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncAll) : null;
    ro?.observe(root);
    window.addEventListener('resize', syncAll);
    return () => {
      cancelAnimationFrame(raf);
      root.removeEventListener('scroll', onScroll, true);
      mo.disconnect();
      ro?.disconnect();
      window.removeEventListener('resize', syncAll);
    };
  }, [detailedFilterOpen, activeChips.length, isNarrowViewport]);

  return (
    <div
      ref={panelRootRef}
      className={barVariant === 'plain' ? 'mb-0' : 'mb-2'}
      lang={i18n.language}
    >
      <KeywordDetailedFilterBar
        keywordId="filter-keyword-search"
        keyword={localFilters.search || ''}
        onKeywordChange={handleKeywordChange}
        keywordPlaceholder={t(
          isNarrowViewport
            ? 'sample_filter.keyword_placeholder_short'
            : 'sample_filter.keyword_placeholder'
        )}
        keywordAriaLabel={
          keywordAriaLabel ??
          t('sample_filter.search_aria', {
            defaultValue: t('sample_filter.keyword_search'),
          })
        }
        keywordTitle={t('sample_filter.search_title', {
          defaultValue: t('sample_filter.keyword_placeholder'),
        })}
        detailedOpen={detailedFilterOpen}
        detailedCount={detailedCount}
        onToggleDetailed={toggleDetailedFilter}
        onClear={handleClear}
        hideKeywordLabel
        toolbar={toolbar}
        mid={mid}
        trailing={trailing}
        variant={barVariant}
        detailedPanel={
          <SampleDetailedFilterForm
            localFilters={localFilters}
            isAdmin={isAdmin}
            hideLocationFilters={hideLocationFilters}
            storageRooms={storageRooms}
            moreFiltersOpen={moreFiltersOpen}
            preface={detailedPreface}
            onSubmit={handleSubmit}
            onSelectChange={handleSelectFilterChange}
            onDebouncedChange={handleDebouncedChange}
            onNotesChange={handleNotesChange}
            onNotesIsEmptyChange={handleNotesIsEmptyChange}
          />
        }
      />
      {activeChips.length > 0 ? (
        <div
          className="sample-filter-chips"
          role="list"
          aria-label={t('sample_filter.active_chips_aria')}
        >
          {activeChips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              role="listitem"
              className="sample-filter-chip"
              onClick={() => handleClearChip(chip.keys)}
              title={t('sample_filter.remove_chip', { label: chip.label })}
              aria-label={t('sample_filter.remove_chip', { label: chip.label })}
            >
              <span className="sample-filter-chip-label">{chip.label}</span>
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
